import { useCallback, useContext } from "react";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";
import {
  QuoteItem,
  quoteItemUpdateFinished,
  quoteItemUpdateStarted,
} from "../../../../../reducers/uploadedBoard";
import { useBoardFromContext } from "../board";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../state-machine/sendBoardEvent";
import { useAppDispatch, useAppSelector } from "../../../store";

export interface QuoteItemUpdate extends QuoteItem {
  markup: number;
  // null for top-level items such as Tariffs
  parent: QuoteItem | null;
}

interface QuoteItemUpdatePayload {
  id: string;
  name: string;
  amount: number;
  markup: number;
  base_price: number;
  parent: QuoteItem | null;
}

interface QuoteItemEventUpdate {
  updated_quote_item?: QuoteItemUpdatePayload;
  quote_item_to_add?: QuoteItemUpdatePayload;
  quote_item_to_remove?: QuoteItemUpdatePayload;
}

const toPayload = (update: QuoteItemUpdate): QuoteItemUpdatePayload => {
  return {
    id: update.id,
    name: update.name,
    amount: update.amount,
    markup: update.markup,
    base_price: update.base_price,
    parent: update.parent,
  };
};

export const useUpdateQuoteItem = () => {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const { board, isDemoBoard } = useBoardFromContext();
  const { sendBoardEvent } = useSendBoardEvent();
  const dispatch = useAppDispatch();
  // Shared across every caller for the board, so tables and rows agree on
  // whether an update is in flight.
  const updatingItem = useAppSelector(
    (state) =>
      (state.uploadedBoard.boards[boardId]?.quoteItemUpdatesInFlight || 0) > 0,
  );
  const currency = board?.quote.currency;

  const sendItemEvent = useCallback(
    (update: QuoteItemEventUpdate) => {
      if (!boardId || isDemoBoard) {
        return;
      }
      dispatch(quoteItemUpdateStarted({ boardId }));
      void sendBoardEvent(boardId, {
        machine_group: MachineGroup.BOARD,
        model_id: boardId,
        event: BoardTransitionEvent.SET_CUSTOM_QUOTE_ITEM,
        update: { ...update, currency },
      }).finally(() => {
        dispatch(quoteItemUpdateFinished({ boardId }));
      });
    },
    [boardId, isDemoBoard, sendBoardEvent, currency, dispatch],
  );

  const updateQuoteItem = useCallback(
    (update: QuoteItemUpdate) => {
      sendItemEvent({ updated_quote_item: toPayload(update) });
    },
    [sendItemEvent],
  );

  const addQuoteItem = useCallback(
    (update: QuoteItemUpdate) => {
      sendItemEvent({ quote_item_to_add: toPayload(update) });
    },
    [sendItemEvent],
  );

  const removeQuoteItem = useCallback(
    (update: QuoteItemUpdate) => {
      sendItemEvent({ quote_item_to_remove: toPayload(update) });
    },
    [sendItemEvent],
  );

  return {
    updateQuoteItem,
    removeQuoteItem,
    addQuoteItem,
    updatingItem,
  };
};
